import {
  cloneElement,
  createContext,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useEffect,
  useContext,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "@/shared/lib/cn";
import { Surface } from "./Surface";

// React ancestry survives portals, unlike DOM containment. Parent popovers keep
// child menus open while their focus and pointer events occur in document.body.
const PopoverBranch = createContext<readonly string[]>([]);

/**
 * A lightweight anchored popover: a trigger plus a floating paper panel that
 * closes on outside-pointer / Escape. The panel is portaled to `document.body`
 * and `position: fixed`, measured from the trigger via `side` / `align` — so it
 * always floats above app chrome and never gets clipped by a scrolling ancestor
 * (the sticky composer, the sidebar list). Compose richer menus on top of this.
 */
export interface PopoverProps {
  /** Renders the trigger; wire `toggle`/`open` into your button. */
  trigger: (args: {
    open: boolean;
    toggle: () => void;
    close: () => void;
    panelId: string;
  }) => ReactNode;
  /** Panel content; receives `close` so items can dismiss after acting. */
  children: (args: { close: () => void; panelId: string }) => ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "end";
  /** Accessible label for the panel region. */
  label: string;
  /** Menus/listboxes provide their own role; `null` avoids a nested dialog. */
  panelRole?: "dialog" | null;
  /** Menu/listbox triggers open with ArrowDown/ArrowUp per APG. */
  openOnArrowKeys?: boolean;
  /** Reports actual open-state changes to composites that manage panel state. */
  onOpenChange?: (open: boolean) => void;
  /** A composite menu can own which sibling branch is open. */
  open?: boolean;
  /** Pointer hover can reveal a submenu without moving keyboard focus. */
  focusOnOpen?: boolean;
  /** Extra classes for the panel (e.g. width). */
  panelClassName?: string;
  className?: string;
}

interface PanelPosition {
  top?: number;
  bottom?: number;
  left?: number;
  maxHeight: number;
}

interface PopoverTriggerProps {
  "aria-controls"?: string;
  "aria-expanded"?: boolean;
}

export function Popover({
  trigger,
  children,
  side = "bottom",
  align = "start",
  label,
  panelRole = "dialog",
  openOnArrowKeys = false,
  onOpenChange,
  open: controlledOpen,
  focusOnOpen = true,
  panelClassName,
  className,
}: PopoverProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = (value: boolean | ((previous: boolean) => boolean)) => {
    const next = typeof value === "function" ? value(open) : value;
    if (controlledOpen === undefined) setUncontrolledOpen(next);
    else if (next !== open) onOpenChange?.(next);
  };
  const [pos, setPos] = useState<PanelPosition | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const restoreFocusRef = useRef(false);
  const initialFocusEdgeRef = useRef<"first" | "last">("first");
  const reportedOpenRef = useRef(open);
  const panelId = useId();
  const parentBranch = useContext(PopoverBranch);
  const branch = [...parentBranch, panelId];

  useEffect(() => {
    if (reportedOpenRef.current === open) return;
    reportedOpenRef.current = open;
    if (controlledOpen === undefined) onOpenChange?.(open);
  }, [controlledOpen, onOpenChange, open]);

  const hasOpenChildPanel = () => Array.from(
    document.querySelectorAll<HTMLElement>("[data-popover-branch]"),
  ).some(panel => {
    const ancestry = panel.dataset.popoverBranch?.split(" ") ?? [];
    return ancestry.includes(panelId) && ancestry.at(-1) !== panelId;
  });

  const close = () => {
    restoreFocusRef.current = true;
    setOpen(false);
  };
  const rememberTrigger = () => {
    const activeElement = document.activeElement;
    triggerRef.current =
      activeElement instanceof HTMLElement &&
      rootRef.current?.contains(activeElement)
        ? activeElement
        : (rootRef.current?.querySelector<HTMLElement>(
            'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
          ) ?? null);
  };
  const toggle = () => {
    if (!open) {
      initialFocusEdgeRef.current = "first";
      rememberTrigger();
    }
    setOpen((value) => !value);
  };

  // Anchor the fixed panel to the trigger and cap it to the space on its opening
  // side so it never spills past the viewport. Recomputed on resize/scroll
  // because the trigger (docked composer) can move.
  useLayoutEffect(() => {
    if (!open) return;
    rememberTrigger();
    const GAP = 4;
    const EDGE = 12; // breathing room from the viewport edge
    const MIN_ROOM = 240; // below this, prefer the roomier side
    const measure = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const panelRect = panelRef.current?.getBoundingClientRect();
      const panelWidth = panelRect?.width ?? 0;
      if (side === "left" || side === "right") {
        const spaceRight = vw - rect.right - GAP - EDGE;
        const spaceLeft = rect.left - GAP - EDGE;
        const openLeft = side === "left"
          ? !(spaceLeft < panelWidth && spaceRight > spaceLeft)
          : spaceRight < panelWidth && spaceLeft > spaceRight;
        const desiredLeft = openLeft ? rect.left - panelWidth - GAP : rect.right + GAP;
        const panelHeight = panelRect?.height ?? 0;
        const desiredTop = align === "end" ? rect.bottom - panelHeight : rect.top;
        const next: PanelPosition = {
          left: Math.max(EDGE, Math.min(desiredLeft, vw - panelWidth - EDGE)),
          top: Math.max(EDGE, Math.min(desiredTop, vh - panelHeight - EDGE)),
          maxHeight: Math.max(0, vh - EDGE * 2),
        };
        setPos(previous => previous?.left === next.left && previous?.top === next.top &&
          previous?.maxHeight === next.maxHeight ? previous : next);
        return;
      }
      const spaceBelow = vh - rect.bottom - GAP - EDGE;
      const spaceAbove = rect.top - GAP - EDGE;
      // Open on the requested side, but flip when it's too cramped and the other
      // side is roomier — so a control low in a scrolling panel opens upward
      // instead of squashing into a few scrolling rows.
      const openTop =
        side === "top"
          ? !(spaceAbove < MIN_ROOM && spaceBelow > spaceAbove)
          : spaceBelow < MIN_ROOM && spaceAbove > spaceBelow;
      const next: PanelPosition = {
        maxHeight: Math.max(0, Math.min(vh - EDGE * 2, openTop ? spaceAbove : spaceBelow)),
      };
      if (openTop) next.bottom = vh - rect.top + GAP;
      else next.top = rect.bottom + GAP;
      const rtl = getComputedStyle(rootRef.current!).direction === "rtl";
      const anchorRight = (align === "end") !== rtl;
      const desiredLeft = anchorRight ? rect.right - panelWidth : rect.left;
      next.left = Math.max(EDGE, Math.min(desiredLeft, vw - panelWidth - EDGE));
      setPos((previous) => previous?.left === next.left && previous?.top === next.top &&
        previous?.bottom === next.bottom && previous?.maxHeight === next.maxHeight ? previous : next);
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (rootRef.current) observer?.observe(rootRef.current);
    if (panelRef.current) observer?.observe(panelRef.current);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, side, align, panelClassName]);

  useEffect(() => {
    if (!open) return;
    const focusFrame = requestAnimationFrame(() => {
      if (hasOpenChildPanel()) return;
      const selector =
        initialFocusEdgeRef.current === "last"
          ? 'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
          : 'button:not(:disabled):not([tabindex="-1"]), [href]:not([tabindex="-1"]), input:not(:disabled):not([tabindex="-1"]), select:not(:disabled):not([tabindex="-1"]), textarea:not(:disabled):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])';
      const focusable = Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(selector) ?? [],
      );
      if (!focusOnOpen) return;
      (initialFocusEdgeRef.current === "last"
        ? focusable.at(-1)
        : focusable[0]
      )?.focus();
    });
    function containsTarget(target: EventTarget | null) {
      if (!(target instanceof Node)) return false;
      return rootRef.current?.contains(target) || panelRef.current?.contains(target) ||
        (target instanceof Element && target.closest<HTMLElement>("[data-popover-branch]")?.dataset.popoverBranch?.split(" ").includes(panelId));
    }
    function onPointerDown(event: PointerEvent) {
      if (!containsTarget(event.target)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && !event.defaultPrevented && !hasOpenChildPanel()) {
        event.preventDefault();
        restoreFocusRef.current = true;
        setOpen(false);
      }
    }
    function onFocusIn(event: FocusEvent) {
      if (!containsTarget(event.target)) setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [open, panelId, focusOnOpen]);

  useEffect(() => {
    if (open || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    triggerRef.current?.focus();
  }, [open]);

  const triggerNode = trigger({ open, toggle, close, panelId });

  return (
    <PopoverBranch.Provider value={branch}>
    <div
      ref={rootRef}
      className={cn("relative inline-flex", className)}
      onKeyDown={(event) => {
        if (
          open ||
          !openOnArrowKeys ||
          (event.key !== "ArrowDown" && event.key !== "ArrowUp")
        )
          return;
        event.preventDefault();
        initialFocusEdgeRef.current =
          event.key === "ArrowUp" ? "last" : "first";
        rememberTrigger();
        setOpen(true);
      }}
    >
      {isValidElement(triggerNode)
        ? cloneElement(triggerNode as ReactElement<PopoverTriggerProps>, {
            "aria-controls": panelId,
            "aria-expanded": open,
          })
        : triggerNode}
      {open
        ? createPortal(
            <div
              ref={panelRef}
              data-popover-branch={branch.join(" ")}
              onKeyDown={(event) => {
                if (event.key !== "Escape" || hasOpenChildPanel()) return;
                event.preventDefault();
                event.stopPropagation();
                close();
              }}
              style={{
                position: "fixed",
                top: pos?.top,
                bottom: pos?.bottom,
                left: pos?.left,
                maxWidth: "calc(100vw - 24px)",
                visibility: pos ? undefined : "hidden",
              }}
              className="z-50"
            >
              <Surface
                id={panelRole ? panelId : undefined}
                role={panelRole ?? undefined}
                aria-label={panelRole ? label : undefined}
                tone="glass"
                radius="lg"
                border="none"
                shadow="soft"
                style={{ maxHeight: pos?.maxHeight, maxWidth: "calc(100vw - 24px)" }}
                className={cn(
                  "studio-overlay studio-popover flex min-h-0 min-w-[200px] flex-col overflow-hidden overscroll-contain",
                  panelClassName,
                )}
              >
                {children({ close, panelId })}
              </Surface>
            </div>,
            document.body,
          )
        : null}
    </div>
    </PopoverBranch.Provider>
  );
}
